import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import httpClient from "../../lib/httpClient";
import { Grid, Box, Card } from "@material-ui/core";
import {
  tableCellRender,
  dateFilterOperators,
  stringFilterOperators,
  formatDateValue,
  getLatestUpdateAgro,
  filterModelToQueryValue,
  getCompanyCodeName,
  isSingleSelectOperators,
  getCompanyCodeFilter,
} from "../util/tableUtils";

import { UniversalMap } from "../map/UniversalMap";
import { UpdateMapButton } from "../util/UpdateMapButton";
import { DataGridCystellarTable } from "../util/DataGridCystellarTable";
import { saveUserMonitorData } from "../util/userUtils";
import { useClientConfig } from "../../context/clientConfigContext";

export const AgriculturePoliciesList = () => {
  const { t } = useTranslation();
  const config = useClientConfig();

  const [agriculturePoliciesData, setAgriculturePoliciesData] = useState([]);
  const [tableState, setTableState] = useState({
    isLoading: false,
    total: 0,
  });

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 25,
  });
  const [filterModel, setFilterModel] = useState({});
  const [sortModel, setSortModel] = useState({});
  const [filteredMapData, setFilteredMapData] = useState(null);
  const [lastUpdateDateMsg, setLastUpdateDateMsg] = useState("");

  const onFilterChange = useCallback((aFilterModel) => {
    setFilterModel(aFilterModel);
  }, []);

  const onSortChange = useCallback((aSortModel) => {
    setSortModel(aSortModel);
  }, []);

  const onTableRowClick = (rowData) => {
    let wnd = window.open(`#/policiesagriculture/${rowData.row.policyNumber}`, "_blank");
    wnd.onload = function () {
      wnd.document.title = `${rowData.row.policyNumber}`;
    };
  };

  useEffect(() => {
    getLatestUpdateAgro("/serviceLogs/agriculture?service=agricultureDb_contratti").then((lastUpdateMsg) => {
      setLastUpdateDateMsg(lastUpdateMsg);
    });
    saveUserMonitorData("agriculturePoliciesList", "");
  }, []);

  useEffect(() => {
    const getPoliciesData = async () => {
      setTableState((old) => ({ ...old, isLoading: true }));
      let sortField, sortDirection;
      const sortItem = sortModel;
      if (sortItem && Object.keys(sortItem).length) {
        const { field, sort } = sortItem[0];
        sortField = field;
        sortDirection = sort;
      }

      const { data: policiesResponse } = await httpClient().get(
        `/agriculturePolicies?page=${paginationModel.page}&pageSize=${
          paginationModel.pageSize
        }&filters=${filterModelToQueryValue(filterModel)}&orderBy=${sortField}&orderDirection=${sortDirection}`,
      );

      setAgriculturePoliciesData(policiesResponse.data);
      setTableState((old) => ({
        ...old,
        isLoading: false,
        total: policiesResponse.total,
      }));
    };

    if (filterModel) {
      const isAnyIncompleteFilter = filterModel?.items?.some((filter) => !filter?.value);

      if (!isAnyIncompleteFilter) {
        getPoliciesData();
      }
    } else {
      getPoliciesData();
    }
  }, [filterModel, sortModel, paginationModel.page, paginationModel.pageSize]);

  const agriculturePoliciesColumns = [
    {
      field: "seasonDescription",
      headerName: t("agriculture.season"),
    },
    {
      field: "referenceDate",
      headerName: t("agriculture.year"),
    },
    {
      field: "policyNumber",
      headerName: t("agriculture.policyNo"),
      sortable: false,
    },
    {
      field: "companyCode",
      headerName: t("company"),
      sortable: false,
      type: "singleSelect",
      filterOperators: isSingleSelectOperators,
      valueOptions: getCompanyCodeFilter(config.agriCompanyCodesMap),
      valueGetter: (params) => getCompanyCodeName(params.row.companyCode, config.agriCompanyCodesMap),
    },
    { field: "agencyCode", headerName: t("agencyCode"), sortable: false },
    { field: "agencyDescription", headerName: t("agency") },
    {
      field: "subagencyCode",
      headerName: t("subagencyCode"),
      sortable: false,
    },
    {
      field: "addressCity",
      headerName: t("agriculture.municipality"),
      sortable: false,
    },
    {
      field: "addressProvince",
      headerName: t("agriculture.province"),
    },
    {
      field: "addressRegion",
      headerName: t("agriculture.region"),
      sortable: false,
    },
    {
      field: "plantSpecies",
      headerName: t("agriculture.specie"),
      sortable: false,
    },
    {
      field: "plantMacropecies",
      headerName: t("agriculture.macrospecie"),
      sortable: false,
    },
    {
      field: "dateNotification",
      headerName: t("agriculture.notificationDate"),
      type: "date",
      filterOperators: dateFilterOperators,
      valueGetter: (params) => params.row.dateNotification && formatDateValue(params.row.dateNotification),
    },
    {
      field: "nsx",
      headerName: t("agriculture.nsx"),
    },
  ];

  const columns = agriculturePoliciesColumns.map((tableColumn) => ({
    valueFormatter: (params) => tableCellRender(params.value?.toString()),
    filterOperators: stringFilterOperators,
    flex: 1,
    ...tableColumn,
  }));

  return (
    <Grid style={{ marginTop: 25 }}>
      <Grid container spacing={1}>
        <Grid item xs={12} sm={12}>
          <Box>
            <Card>
              <UniversalMap
                page={"agriculture-policies"}
                filteredMapDataProp={filteredMapData}
                setFilteredMapDataProp={setFilteredMapData}
                routeNameProp="agriculturePolicies"
              />
            </Card>
          </Box>
        </Grid>
        <Grid item xs={12} sm={12}>
          <div style={{ height: 700, width: "100%" }}>
            <DataGridCystellarTable
              columns={columns}
              rows={agriculturePoliciesData}
              rowCount={tableState.total}
              getRowId={(rowData) => rowData._id}
              onRowClick={onTableRowClick}
              loading={tableState.isLoading}
              paginationMode={"server"}
              paginationModel={paginationModel}
              onPaginationModelChange={(newPaginationModel) => setPaginationModel(newPaginationModel)}
              filterMode={"server"}
              onFilterModelChange={onFilterChange}
              sortingMode={"server"}
              onSortModelChange={onSortChange}
              customToolbarComp={lastUpdateDateMsg ? true : false}
              customToolbarCompMsg={lastUpdateDateMsg}
              customToolbarWidgets={
                <UpdateMapButton
                  setFilteredMapData={setFilteredMapData}
                  filterModel={filterModel}
                  routeName="agriculturePolicies"
                />
              }
            />
          </div>
        </Grid>
      </Grid>
    </Grid>
  );
};
